import { AxiosError } from 'axios';
import apiClient, { handleApiResponse } from './client';
import type {
  MappingConfig,
  MappingRequest,
  ApiResponse,
  SourceSystem,
} from '@types';

export const mappingApi = {
  // Get all mapping configurations
  getAllMappings: async (): Promise<MappingConfig[]> => {
    try {
      const response =
        await apiClient.get<
          ApiResponse<
            | MappingConfig[]
            | { data: MappingConfig[] }
            | { mappings: MappingConfig[] }
          >
        >('/v1/mappings');
      const data = handleApiResponse(response);
      // Handle: direct array, paginated { data: [...] }, or legacy { mappings: [...] }
      if (Array.isArray(data)) {
        return data;
      }
      if (data && typeof data === 'object') {
        // Paginated response: { data: [...], page, total, ... }
        if (
          'data' in data &&
          Array.isArray((data as { data: MappingConfig[] }).data)
        ) {
          return (data as { data: MappingConfig[] }).data;
        }
        // Legacy response: { mappings: [...] }
        if (
          'mappings' in data &&
          Array.isArray((data as { mappings: MappingConfig[] }).mappings)
        ) {
          return (data as { mappings: MappingConfig[] }).mappings;
        }
      }
      return [];
    } catch (error) {
      // Return empty array for 404 (no mappings yet) or 401 (auth issues during dev)
      if (
        error instanceof AxiosError &&
        [401, 404].includes(error.response?.status ?? 0)
      ) {
        return [];
      }
      throw error;
    }
  },

  // Get mapping configuration by ID
  getMappingById: async (id: string): Promise<MappingConfig> => {
    const response = await apiClient.get<ApiResponse<MappingConfig>>(
      `/v1/mappings/${id}`
    );
    return handleApiResponse(response);
  },

  // Get mappings by source system
  getMappingsBySystem: async (
    sourceSystem: SourceSystem
  ): Promise<MappingConfig[]> => {
    const response = await apiClient.get<
      ApiResponse<
        | MappingConfig[]
        | { data: MappingConfig[] }
        | { mappings: MappingConfig[] }
      >
    >(`/v1/mappings/system/${sourceSystem}`);
    const data = handleApiResponse(response);
    // Handle: direct array, paginated { data: [...] }, or legacy { mappings: [...] }
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === 'object') {
      if (
        'data' in data &&
        Array.isArray((data as { data: MappingConfig[] }).data)
      ) {
        return (data as { data: MappingConfig[] }).data;
      }
      if (
        'mappings' in data &&
        Array.isArray((data as { mappings: MappingConfig[] }).mappings)
      ) {
        return (data as { mappings: MappingConfig[] }).mappings;
      }
    }
    return [];
  },

  // Create new mapping configuration
  createMapping: async (data: MappingRequest): Promise<MappingConfig> => {
    const response = await apiClient.post<ApiResponse<MappingConfig>>(
      '/v1/mappings',
      data
    );
    return handleApiResponse(response);
  },

  // Update mapping configuration
  updateMapping: async (
    id: string,
    data: Partial<MappingRequest>
  ): Promise<MappingConfig> => {
    const response = await apiClient.put<ApiResponse<MappingConfig>>(
      `/v1/mappings/${id}`,
      data
    );
    return handleApiResponse(response);
  },

  // Delete mapping configuration
  deleteMapping: async (id: string): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/v1/mappings/${id}`
    );
    return handleApiResponse(response);
  },

  // Set mapping as default
  setDefaultMapping: async (id: string): Promise<MappingConfig> => {
    const response = await apiClient.post<ApiResponse<MappingConfig>>(
      `/v1/mappings/${id}/set-default`,
      {}
    );
    return handleApiResponse(response);
  },

  // Get default mapping for source system
  getDefaultMapping: async (
    sourceSystem: SourceSystem
  ): Promise<MappingConfig | null> => {
    try {
      const response = await apiClient.get<ApiResponse<MappingConfig>>(
        `/v1/mappings/default/${sourceSystem}`
      );
      return handleApiResponse(response);
    } catch {
      return null;
    }
  },
};

export default mappingApi;
